import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  PlusCircle,
  ChevronRight,
  ChevronDown,
  Car,
  Receipt,
  Briefcase,
  Layers,
  X,
  RefreshCw,
} from 'lucide-react';
import { useClaims, useCreateClaim, type Claim, type ClaimType } from '../hooks/useClaims';
import { ApiError } from '../lib/api';
import { Card, Button, cn, ClaimStatusBadge, EmptyState, ErrorState } from '@rhpc/ui';
import { PageHeader, PageShell } from '@/components/PageShell';

function formatMonthLabel(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    year: 'numeric',
    month: 'long',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

function formatMonthKey(isoDate: string) {
  const d = new Date(isoDate);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

/** Full on desktop; shortened on mobile e.g. RHPC-202608-GEN-0001 → RHPC…0001 */
function ReferenceCell({ referenceNumber }: { referenceNumber: string | null }) {
  if (!referenceNumber) return <>—</>;
  const parts = referenceNumber.split('-');
  const short =
    parts.length >= 2 ? `${parts[0]}…${parts[parts.length - 1]}` : referenceNumber;
  return (
    <>
      <span className="md:hidden">{short}</span>
      <span className="hidden md:inline">{referenceNumber}</span>
    </>
  );
}

export const CLAIM_TYPE_CONFIG: Record<
  ClaimType,
  { label: string; description: string; badgeClass: string; icon: LucideIcon }
> = {
  GENERAL: {
    label: 'General',
    description: 'Combined travel and miscellaneous expenses',
    badgeClass: 'bg-surface-2 text-text-2',
    icon: Layers,
  },
  MILEAGE: {
    label: 'Mileage',
    description: 'Travel mileage, tolls, and parking',
    badgeClass: 'bg-info-soft text-info',
    icon: Car,
  },
  PETTY_CASH: {
    label: 'Petty Cash',
    description: 'Office purchases & out-of-pocket receipts',
    badgeClass: 'bg-primary-soft text-primary-text',
    icon: Receipt,
  },
  PROJECT: {
    label: 'Project',
    description: 'Dedicated client or engagement expenses',
    badgeClass: 'bg-warn-soft text-warn',
    icon: Briefcase,
  },
};

export function ClaimTypeBadge({ type }: { type?: ClaimType | string }) {
  const config = CLAIM_TYPE_CONFIG[(type as ClaimType) ?? 'GENERAL'] ?? CLAIM_TYPE_CONFIG.GENERAL;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold', config.badgeClass)}>
      {config.label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  return <ClaimStatusBadge status={status} />;
}

const currentMonthLabel = new Intl.DateTimeFormat('en-MY', {
  month: 'long',
  year: 'numeric',
  timeZone: 'Asia/Kuala_Lumpur',
}).format(new Date());

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function ClaimsSkeleton() {
  return (
    <Card className="overflow-hidden p-0">
      <div className="px-6 pb-4 pt-5">
        <div className="skeleton h-4 w-[120px] rounded-[5px]" />
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-y border-border">
            <th className="px-6 py-[10px] text-left text-xs font-medium text-text-2">Month</th>
            <th className="px-3 py-[10px] text-left text-xs font-medium text-text-2">Description</th>
            <th className="px-3 py-[10px] text-left text-xs font-medium text-text-2">Type</th>
            <th className="px-3 py-[10px] text-left text-xs font-medium text-text-2">Reference</th>
            <th className="px-3 py-[10px] text-right text-xs font-medium text-text-2">Amount (RM)</th>
            <th className="px-4 py-[10px] text-left text-xs font-medium text-text-2">Status</th>
            <th className="w-11" />
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2, 3, 4].map((i) => (
            <tr key={i} className="border-b border-border last:border-b-0">
              <td className="px-6 py-4">
                <div className="skeleton h-[13px] w-20 rounded" />
              </td>
              <td className="px-3 py-4">
                <div className="skeleton h-[13px] w-32 rounded" />
              </td>
              <td className="px-3 py-4">
                <div className="skeleton h-[20px] w-16 rounded-full" />
              </td>
              <td className="px-3 py-4">
                <div className="skeleton h-[13px] w-[130px] rounded" />
              </td>
              <td className="px-3 py-4">
                <div className="skeleton ml-auto h-[13px] w-[70px] rounded" />
              </td>
              <td className="px-4 py-4">
                <div className="skeleton h-[22px] w-[90px] rounded-full" />
              </td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ClaimsEmpty({ onOpenModal }: { onOpenModal: () => void }) {
  return (
    <EmptyState
      title="No claims yet"
      description={`You haven't created any expense claims. Start your ${currentMonthLabel} claim to begin adding mileage and petty-cash expenses.`}
      action={
        <Button className="gap-2" onClick={onOpenModal}>
          <PlusCircle className="h-[17px] w-[17px]" strokeWidth={2.2} />
          Create Claim
        </Button>
      }
    />
  );
}

function ClaimsError({ onRetry }: { onRetry: () => void }) {
  return (
    <ErrorState
      title="Couldn't load your claims"
      happened="Something went wrong while fetching your claim history."
      doing="You can retry the request now."
      youCan="Check your connection and try again. If it keeps happening, contact IT support."
      actions={
        <>
          <Button onClick={onRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" strokeWidth={2} />
            Try again
          </Button>
          <Button variant="secondary">Contact IT</Button>
        </>
      }
    />
  );
}

export function ClaimsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');

  const { data, isLoading, isError, refetch } = useClaims();
  const createClaim = useCreateClaim();
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue());
  const [selectedType, setSelectedType] = useState<ClaimType>('GENERAL');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const claims = data?.claims ?? [];

  const monthOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of claims) {
      const key = formatMonthKey(c.claimMonth);
      if (!seen.has(key)) seen.set(key, formatMonthLabel(c.claimMonth));
    }
    return Array.from(seen.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [claims]);

  const filtered = useMemo(() => {
    let list = [...claims].sort((a, b) => {
      // Sort primarily by claimMonth desc, then createdAt desc
      const monthCmp = b.claimMonth.localeCompare(a.claimMonth);
      if (monthCmp !== 0) return monthCmp;
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    });
    if (monthFilter !== 'all') {
      list = list.filter((c) => formatMonthKey(c.claimMonth) === monthFilter);
    }
    if (typeFilter !== 'all') {
      list = list.filter((c) => (c.claimType ?? 'GENERAL') === typeFilter);
    }
    return list;
  }, [claims, monthFilter, typeFilter]);

  const handleOpenModal = () => {
    setSelectedMonth(currentMonthValue());
    setSelectedType('GENERAL');
    setDescription('');
    setIsNewModalOpen(true);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      const result = await createClaim.mutateAsync({
        claimMonth: selectedMonth,
        claimType: selectedType,
        description: description.trim() || undefined,
      });
      setIsNewModalOpen(false);
      navigate(`/claims/${result.claim.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create claim. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="My Claims"
        description="View and manage your expense and travel claims."
        actions={
          !isLoading ? (
            <Button className="gap-2" onClick={handleOpenModal}>
              <PlusCircle className="h-4 w-4" strokeWidth={2.2} />
              New Claim
            </Button>
          ) : undefined
        }
      />

      {error && (
        <div className="mb-6 rounded-lg border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {isLoading && <ClaimsSkeleton />}

      {!isLoading && isError && <ClaimsError onRetry={() => refetch()} />}

      {!isLoading && !isError && claims.length === 0 && (
        <ClaimsEmpty onOpenModal={handleOpenModal} />
      )}

      {!isLoading && !isError && claims.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="flex flex-col gap-4 px-6 pb-4 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold">All claims</h2>
              <p className="mt-[3px] text-[13px] text-text-2">
                Showing {filtered.length} claim{filtered.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              {/* Type Filter */}
              <div className="relative">
                <select
                  aria-label="Filter by type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-[38px] cursor-pointer appearance-none rounded-md border border-input bg-surface py-0 pl-3.5 pr-[34px] text-[13px] font-medium text-text focus:outline-2 focus:outline-primary focus:outline-offset-1"
                >
                  <option value="all">All Types</option>
                  <option value="GENERAL">General</option>
                  <option value="MILEAGE">Mileage</option>
                  <option value="PETTY_CASH">Petty Cash</option>
                  <option value="PROJECT">Project</option>
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-[11px] top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-2"
                  strokeWidth={2}
                />
              </div>

              {/* Month Filter */}
              <div className="relative">
                <select
                  aria-label="Filter by month"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="h-[38px] cursor-pointer appearance-none rounded-md border border-input bg-surface py-0 pl-3.5 pr-[34px] text-[13px] font-medium text-text focus:outline-2 focus:outline-primary focus:outline-offset-1"
                >
                  <option value="all">All Months</option>
                  {monthOptions.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-[11px] top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-2"
                  strokeWidth={2}
                />
              </div>
            </div>
          </div>

          <table className="hidden w-full border-collapse md:table">
            <thead>
              <tr className="bg-surface-2 text-[11px] font-bold uppercase tracking-[.12em] text-text-3">
                <th className="px-6 py-3 text-left">Month</th>
                <th className="px-3 py-3 text-left">Description</th>
                <th className="px-3 py-3 text-left">Type</th>
                <th className="px-3 py-3 text-left">Reference</th>
                <th className="px-3 py-3 text-right">Amount (RM)</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="w-11" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((claim: Claim) => (
                <tr
                  key={claim.id}
                  id={`claim-${claim.id}`}
                  onClick={() => navigate(`/claims/${claim.id}`)}
                  className={cn(
                    'cursor-pointer border-b border-border transition-colors last:border-b-0 hover:bg-surface-2/50',
                    highlightId === claim.id && 'bg-info-soft/30'
                  )}
                >
                  <td className="whitespace-nowrap px-6 py-[14px] text-sm font-medium">
                    {formatMonthLabel(claim.claimMonth)}
                  </td>
                  <td className="max-w-[220px] px-3 py-[14px] text-sm text-text-2 truncate" title={claim.description ?? ''}>
                    {claim.description || <span className="text-text-2/40">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-[14px]">
                    <ClaimTypeBadge type={claim.claimType} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-[14px] font-mono text-[13px] text-text-2">
                    <ReferenceCell referenceNumber={claim.referenceNumber} />
                  </td>
                  <td className="px-3 py-[14px] text-right font-mono text-sm font-medium">
                    {formatCurrency(Number(claim.totalAmount))}
                  </td>
                  <td className="px-4 py-[14px]">
                    <StatusBadge status={claim.status} />
                  </td>
                  <td className="py-[14px] pl-0 pr-4 text-text-2">
                    <ChevronRight className="h-4 w-4" strokeWidth={2} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="divide-y divide-border md:hidden">
            {filtered.map((claim: Claim) => (
              <button
                key={claim.id}
                id={`claim-card-${claim.id}`}
                type="button"
                onClick={() => navigate(`/claims/${claim.id}`)}
                className={cn(
                  'flex w-full flex-col gap-2 p-4 text-left hover:bg-surface-2',
                  highlightId === claim.id && 'bg-primary-soft'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-text">{formatMonthLabel(claim.claimMonth)}</span>
                  <StatusBadge status={claim.status} />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <ClaimTypeBadge type={claim.claimType} />
                  <span className="font-mono text-sm font-medium text-text">
                    RM {formatCurrency(Number(claim.totalAmount))}
                  </span>
                </div>
                {claim.referenceNumber && (
                  <p className="font-mono text-xs text-text-3">{claim.referenceNumber}</p>
                )}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* New Claim Modal (Issue #8, #9) */}
      {isNewModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          id="new-claim-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs"
        >
          <div className="w-full max-w-lg rounded-xl border border-border bg-surface p-6 shadow-xl animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <h3 className="text-base font-semibold">Create New Expense Claim</h3>
              <button
                type="button"
                onClick={() => setIsNewModalOpen(false)}
                className="rounded-md p-1 text-text-2 hover:bg-surface-2 hover:text-text"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="mt-5 space-y-5">
              <div>
                <label htmlFor="claim-month-input" className="mb-1.5 block text-sm font-medium">
                  Claim Month
                </label>
                <input
                  id="claim-month-input"
                  type="month"
                  value={selectedMonth}
                  max={currentMonthValue()}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">Claim Type</label>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {(Object.keys(CLAIM_TYPE_CONFIG) as ClaimType[]).map((type) => {
                    const cfg = CLAIM_TYPE_CONFIG[type];
                    const Icon = cfg.icon;
                    const isSelected = selectedType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setSelectedType(type)}
                        className={cn(
                          'flex items-start gap-3 rounded-lg border p-3 text-left transition-all',
                          isSelected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary'
                            : 'border-border bg-surface hover:border-border/80 hover:bg-surface-2/50'
                        )}
                      >
                        <div
                          className={cn(
                            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                            isSelected ? 'bg-primary text-white' : 'bg-surface-2 text-text-2'
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-text">{cfg.label}</p>
                          <p className="mt-0.5 text-xs text-text-2 leading-tight">{cfg.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label htmlFor="claim-desc-input" className="mb-1.5 block text-sm font-medium">
                  Description <span className="text-xs text-text-2 font-normal">(Optional)</span>
                </label>
                <input
                  id="claim-desc-input"
                  type="text"
                  placeholder="e.g. On-site client setup in Penang"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setIsNewModalOpen(false)}>
                  Cancel
                </Button>
                <Button id="create-claim-submit-btn" type="submit" loading={creating}>
                  {creating ? 'Creating…' : 'Create Claim'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageShell>
  );
}
